import { Box, Button, Dialog, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Alert from "@mui/material/Alert";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  SkuContentRule,
  Skus,
  ISkuContentRuleProduct,
  ListResponseOfSkuContentRuleProduct,
  SkuContentRuleProductRequest,
  AdminListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import useBoolean from "hooks/useBoolean";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import SkuRuleDetailProductForm from "./SkuRuleDetailProductForm";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";

type SkuRuleDetailProductListProps = {
  sku: Skus;
  skuContentRule: SkuContentRule;
  readonly?: boolean;
  dialogTitleKey: string;
};

type SkuRuleDetailProductsFormValues = {
  products: ISkuContentRuleProduct[];
};

const useStyles = makeStyles({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const { Form } = withTypes<SkuRuleDetailProductsFormValues>();

interface SkuContentRuleProductWithId extends ISkuContentRuleProduct {
  id: string;
}

const SkuRuleDetailProductList = ({
  sku,
  skuContentRule,
  readonly,
  dialogTitleKey,
  ...props
}: SkuRuleDetailProductListProps) => {
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const styles = useStyles(useTheme());
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<ISkuContentRuleProduct | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    skuRuleProductToEdit,
    setSkuRuleProductToEdit,
  ] = useState<ISkuContentRuleProduct | null>(null);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("product.name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(
          skuService.deleteSkuContentRuleProduct(sku.id!, skuContentRule.id, id)
        );
        queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "skuRuleProductFilterDeleted" }))
        );
      } catch (err) {
        console.log(err);
      }
    }
  }, [
    deleting?.id,
    registerPromise,
    skuService,
    sku.id,
    skuContentRule.id,
    queryClient,
    t,
  ]);

  const response = useQuery<ListResponseOfSkuContentRuleProduct>(
    [
      "skuRuleDetails",
      sku.id,
      skuContentRule.id,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      skuService.getSkuContentRuleProducts(
        sku.id!,
        skuContentRule.id,
        AdminListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      )
  );

  const skuContentRuleProducts = useMemo(
    () => (response?.data?.data ?? []) as SkuContentRuleProductWithId[],
    [response?.data?.data]
  );

  const init = useMemo(
    () => ({
      products: skuContentRuleProducts,
    }),
    [skuContentRuleProducts]
  );

  if (response.isLoading) return <Loader />;
  if (response.isError) {
    console.log(response.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleDetailsError" />
        </Alert>
      </Box>
    );
  }

  const onSubmit = async (values: SkuRuleDetailProductsFormValues) => {
    try {
      await registerPromise(
        skuService.saveSkuContentRuleProduct(
          sku.id!,
          skuContentRule.id,
          SkuContentRuleProductRequest.fromJS(values)
        )
      );

      queryClient.invalidateQueries([
        "skuRuleDetails",
        sku.id,
        skuContentRule.id,
      ]);

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "ruleUpdatedMessage" }))
      );
    } catch (err) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSkuRuleEditClose = () => {
    createOpenActions.setFalse();
    setSkuRuleProductToEdit(null);
  };

  const onSkuRuleCreated = () => {
    queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);
    createOpenActions.setFalse();
  };

  const onSkuRuleUpdated = () => {
    queryClient.invalidateQueries(["skuRuleDetails", sku.id!]);
    createOpenActions.setFalse();
  };

  const DeleteButton = ({ value }: { value: ISkuContentRuleProduct }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          disabled={readonly}
          onClick={() => setDeleting(value)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: ISkuContentRuleProduct }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          disabled={readonly}
          onClick={() => {
            setSkuRuleProductToEdit(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "productName",
      customSortAccessor: "product.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "productLabel" }),
    },
    {
      accessor: "productTypeName",
      customSortAccessor: "productType.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "skuFormatName",
      customSortAccessor: "skuFormat.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "formatLabel" }),
    },
    {
      accessor: "skuAvailabilityName",
      customSortAccessor: "SkuAvailability.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "availabilityLabel" }),
    },
    {
      accessor: "skuStatusName",
      customSortAccessor: "skuStatus.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "productVersionName",
      customSortAccessor: "productVersion.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "versionLabel" }),
    },
    {
      accessor: "productYearName",
      customSortAccessor: "productYear.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "yearLabel" }),
    },
    {
      accessor: "corporateEntityName",
      customSortAccessor: "corporateEntity.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "corporateEntityLabel" }),
    },
    {
      accessor: "maxPrice",
      align: "left",
      width: 0,
      type: "price",
      label: t({ id: "maxPriceLabel" }),
    },
    {
      accessor: "minPrice",
      align: "left",
      width: 0,
      type: "price",
      label: t({ id: "minPriceLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  return (
    <>
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, submitError }) => {
          return (
            <form onSubmit={handleSubmit}>
              {!readonly && (
                <Box textAlign="right">
                  <Button
                    onClick={createOpenActions.setTrue}
                    startIcon={<AddIcon />}
                  >
                    <T id="addFilterButton" />
                  </Button>
                </Box>
              )}

              <LazyTable
                {...{
                  rows: skuContentRuleProducts,
                  loading: response.isFetching,
                  dense: true,
                  cells,
                  rowCount: response?.data?.totalRecords ?? 0,
                  page,
                  setPage,
                  order,
                  setOrder,
                  orderBy,
                  setOrderBy,
                  rowsPerPage,
                  setRowsPerPage,
                }}
              />
              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}

              <DeleteEntityDialog
                dialogTitleKey="skuRuleProductFilterDeleteTitle"
                contentKey="skuRuleFilterDeleteQuestion"
                open={!!deleting}
                handleDelete={handleDelete}
                onCancel={() => setDeleting(null)}
              />
              <Dialog
                open={createOpen}
                disableEnforceFocus
                maxWidth="sm"
                fullWidth
              >
                <SkuRuleDetailProductForm
                  onClose={onSkuRuleEditClose}
                  onCreate={onSkuRuleCreated}
                  onUpdate={onSkuRuleUpdated}
                  skuId={skuContentRule.skuId}
                  skuRuleId={skuContentRule.id!}
                  skuContentRuleProduct={skuRuleProductToEdit}
                  dialogTitleKey={dialogTitleKey}
                  {...props}
                />
              </Dialog>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    </>
  );
};

export default SkuRuleDetailProductList;
